// src/pages/Admin/StreamManager.jsx
// Turns the live stream on the Events page on and off.
// Deliberately small: one switch, a few text fields, and a link to go and look.

import { useEffect, useState } from "react";
import { FaTwitch, FaExternalLinkAlt, FaCheck } from "react-icons/fa";
import {
  subscribeToStreamSettings,
  updateStreamSettings,
  DEFAULT_STREAM,
} from "../../lib/siteSettings";

const input =
  "w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-base placeholder-gray-600 focus:outline-none focus:border-red-500 transition-colors";
const labelCls = "block text-sm font-semibold text-gray-300 mb-2";

function StreamManager() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => subscribeToStreamSettings(setSettings), []);

  const set = (field, value) => setSettings((s) => ({ ...s, [field]: value }));

  const save = async (overrides = {}) => {
    setSaving(true);
    setError("");
    try {
      const next = { ...settings, ...overrides };
      setSettings(next);
      await updateStreamSettings({
        enabled: !!next.enabled,
        channel: (next.channel || "").trim(),
        title: (next.title || "").trim() || DEFAULT_STREAM.title,
        subtitle: (next.subtitle || "").trim(),
        showChat: next.showChat !== false,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.message || "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <p className="text-gray-500 animate-pulse">Loading…</p>;

  const isOn = !!settings.enabled;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-xl font-black uppercase tracking-wide text-white">Live Stream</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Show the Twitch stream at the top of the Events page while you're broadcasting.
        </p>
      </div>

      {error && (
        <p className="text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
          {error}
        </p>
      )}

      {/* The switch */}
      <button
        onClick={() => save({ enabled: !isOn })}
        disabled={saving}
        className={`w-full flex items-center justify-between gap-4 px-5 py-5 rounded-xl border-2 transition-colors text-left ${
          isOn
            ? "border-green-500/60 bg-green-500/10"
            : "border-gray-700 bg-gray-900 hover:border-gray-600"
        }`}
      >
        <div>
          <p className="text-white font-bold text-lg">
            {isOn ? "Stream is showing on the site" : "Stream is hidden"}
          </p>
          <p className="text-sm text-gray-400 mt-0.5">
            {isOn
              ? "Visitors can watch on the Events page right now."
              : "Turn this on when you go live."}
          </p>
        </div>
        <span
          className={`flex-shrink-0 w-16 h-9 rounded-full p-1 transition-colors ${
            isOn ? "bg-green-500" : "bg-gray-700"
          }`}
        >
          <span
            className={`block w-7 h-7 bg-white rounded-full transition-transform ${
              isOn ? "translate-x-7" : "translate-x-0"
            }`}
          />
        </span>
      </button>

      {/* Details */}
      <div className="space-y-4 bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div>
          <label className={labelCls}>Twitch channel</label>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm flex-shrink-0">twitch.tv/</span>
            <input
              className={input}
              value={settings.channel || ""}
              placeholder="primevaloresports"
              onChange={(e) => set("channel", e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>Heading</label>
          <input
            className={input}
            value={settings.title || ""}
            placeholder="e.g. Caribbean Cup — Live Now"
            onChange={(e) => set("title", e.target.value)}
          />
        </div>

        <div>
          <label className={labelCls}>
            Small text underneath <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <input
            className={input}
            value={settings.subtitle || ""}
            placeholder="e.g. Grand Final — country vs country"
            onChange={(e) => set("subtitle", e.target.value)}
          />
        </div>

        <button
          onClick={() => set("showChat", settings.showChat === false)}
          className="flex items-center gap-3 text-left"
        >
          <span
            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
              settings.showChat !== false ? "bg-red-600 border-red-600" : "border-gray-600"
            }`}
          >
            {settings.showChat !== false && <FaCheck size={10} className="text-white" />}
          </span>
          <span className="text-sm text-gray-300">
            Show Twitch chat beside the video
            <span className="block text-xs text-gray-500">Hidden on phones either way</span>
          </span>
        </button>

        <div className="flex gap-3 pt-1">
          <button
            onClick={() => save()}
            disabled={saving}
            className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold uppercase tracking-wide rounded-lg transition-colors"
          >
            {saving ? "Saving…" : saved ? "Saved" : "Save changes"}
          </button>
          <a
            href="/events"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-5 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white text-sm font-bold uppercase rounded-lg transition-colors"
          >
            <FaExternalLinkAlt size={11} /> View page
          </a>
        </div>
      </div>

      <p className="text-xs text-gray-600 flex items-start gap-2">
        <FaTwitch className="mt-0.5 flex-shrink-0" />
        <span>
          The player always shows the channel's current broadcast. If you're not live, viewers see
          Twitch's offline screen — so switch this off when the stream ends.
        </span>
      </p>
    </div>
  );
}

export default StreamManager;
